import './App.css';
import mocovax from './mocovax.jpg';
import dmvformula from './dmvformula.png';
import safesight from './safesight.jpg';
import skillsusa from './skillsusa.jpg';
import adam from './adam.jpg';
import swiftmusic from './swift.png';
import { useState, useEffect } from 'react';
import { TypeAnimation } from 'react-type-animation';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import ProjectCard from './ProjectCard';

function App() {
  const [activePage, setActivePage] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsImageVisible(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const sections = ['home', 'about', 'portfolio', 'contact']
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActivePage(entry.target.id);
          }
        });
      },
      { threshold: 0.45, rootMargin: '-20% 0px -35% 0px' }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleNavClick = (sectionId) => {
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
    setActivePage(sectionId);
    setIsMenuOpen(false);
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'contact', label: 'Contact' }
  ];

  const projects = [
    {
      title: 'MoCoVax',
      description: 'Built during vaccine rollout to help people find appointment availability quickly across Montgomery County.',
      impact: '100,000+ unique device visits in the first week.',
      image: mocovax,
      technologies: ['HTML5', 'NodeJS', 'ExpressJS', 'TailwindCSS'],
      category: 'coding'
    },
    {
      title: 'DMV Formula',
      description: 'Created to help families locate baby formula in the DMV during supply shortages with a cleaner discovery flow.',
      image: dmvformula,
      technologies: ['ReactJS', 'TailwindCSS', 'NodeJS', 'Google Cloud Functions', 'ExpressJS'],
      category: 'coding'
    },
    {
      title: 'Swift Music',
      description: 'Desktop music app concept designed for a focused listening experience and reduced playback friction.',
      impact: "Won CodeDay's June 2021 hackathon.",
      image: swiftmusic,
      technologies: ['ElectronJS', 'NodeJS', 'HTML5', 'TailwindCSS', 'ExpressJS'],
      category: 'coding'
    },
    {
      title: 'Project SafeSight',
      description: 'AI-enabled CCTV monitoring concept for recognizing threats and triggering fast community alerts.',
      impact: "Won Bitcamp's 2023 hackathon.",
      image: safesight,
      technologies: ['Firebase', 'NodeJS', 'Python', 'Microsoft Azure', 'ExpressJS', 'ReactJS'],
      category: 'coding'
    },
    {
      title: 'SkillsUSA National Finalist',
      description: 'Competed in national cybersecurity championships after back-to-back state titles in 2022 and 2023.',
      impact: 'Placed 4th in the nation.',
      image: skillsusa,
      technologies: ['Cyber Security', 'Kali Linux', 'Penetration Testing', 'Network Security', 'Cisco'],
      category: 'cybersecurity'
    }
  ];

  const skills = [
    'React',
    'Node.js',
    'Express',
    'Python',
    'Firebase',
    'Azure',
    'Cybersecurity',
    'Penetration Testing',
    'Cloud Functions',
    'Tailwind CSS'
  ];

  const quickStats = [
    { label: 'Hackathon Wins', value: '5+' },
    { label: 'National Placements', value: '5' },
    { label: 'State Championships Won', value: '6+' },
    { label: 'Projects Published', value: '10+' }
  ];

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter((project) => project.category === selectedCategory);

  const currentYear = new Date().getFullYear();

  return (
    <div className="App" style={{ backgroundColor: '#17191E' }}>
      <nav className="bg-[#17191E]/95 backdrop-blur p-3 md:p-4 fixed top-0 w-full border-b border-[#1F2329]" style={{ zIndex: 20 }}>
        <div className="container mx-auto flex items-center justify-between gap-3">
          <div className="text-white text-lg md:text-xl font-mono">
            <span className="px-3 py-2 text-sm md:text-base font-medium">&#47;&#42; Adam Lederer &#42;&#47;</span>
          </div>

          <button onClick={toggleMenu} className="text-white focus:outline-none md:hidden" aria-label="Toggle navigation menu">
            {isMenuOpen ? <XIcon className="h-8 w-8" /> : <MenuIcon className="h-8 w-8" />}
          </button>

          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavClick(item.id)}
                className="px-3 py-2 text-base font-medium text-white"
              >
                <span className={`inline-block ${activePage === item.id ? 'border-b-2 border-[#FF6C4D] pb-1' : 'hover:border-b-2 hover:border-[#FF6C4D] hover:pb-1'}`}>
                  &#47;&#42; {item.label} &#42;&#47;
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden mt-3 border-t border-[#1F2329] pt-3`}>
          {navLinks.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleNavClick(item.id)}
              className="w-full text-left px-3 py-3 text-base font-medium text-white"
            >
              <span className={`inline-block ${activePage === item.id ? 'border-b-2 border-[#FF6C4D] pb-1' : ''}`}>
                &#47;&#42; {item.label} &#42;&#47;
              </span>
            </button>
          ))}
        </div>
      </nav>

      <header className="flex flex-col justify-center items-center min-h-screen pt-24 md:pt-0" id="home" style={{ position: 'relative', zIndex: 1, backgroundColor: '#17191E' }}>
        <div className="particle-bg">
          {Array.from({ length: 18 }).map((_, index) => (
            <span key={index} className="particle" />
          ))}
        </div>
        <div className="container mx-auto flex flex-col md:flex-row justify-center items-center h-full relative px-4 md:px-0">
          <div className="flex justify-center items-center w-full mb-8 md:hidden" style={{ zIndex: 2, position: 'relative' }}>
            <div className={`diamond-container-small transition-opacity duration-1000 ${isImageVisible ? 'opacity-100' : 'opacity-0'}`}>
              <div className="diamond-wrapper">
                <img src={adam} alt="Adam Lederer" className="diamond-image" />
              </div>
            </div>
          </div>
          <div className="flex-1 flex justify-center md:justify-start items-center md:pl-20 w-full md:w-auto mb-8 md:mb-0">
            <div className="max-w-xl text-center md:text-left">
              <p className="text-sm md:text-base tracking-widest uppercase text-[#FFB6A8]">Software Engineer + Cybersecurity Competitor</p>
              <h1 className="text-4xl md:text-6xl font-bold text-white mt-3" style={{ fontFamily: 'Open Sans, sans-serif', zIndex: 2, position: 'relative' }}>
                <TypeAnimation
                  cursor={true}
                  sequence={[
                    'Adam Lederer',
                    2500,
                    'I build practical products.',
                    2200,
                    'I secure systems with care.',
                    2200
                  ]}
                  wrapper="span"
                  repeat={Infinity}
                />
              </h1>
              <p className="text-lg md:text-xl font-light text-gray-300 mt-5 leading-relaxed" style={{ zIndex: 2, position: 'relative' }}>
                I create fast, useful software with a focus on reliability, user impact, and secure-by-default thinking.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center md:justify-start" style={{ zIndex: 2, position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => handleNavClick('portfolio')}
                  className="bg-[#FF6C4D] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#FF8C6D] transition-colors text-center"
                >
                  Explore Work
                </button>
                <button
                  type="button"
                  onClick={() => handleNavClick('contact')}
                  className="bg-transparent text-[#FF6C4D] border-2 border-[#FF6C4D] px-6 py-3 rounded-lg font-medium hover:bg-[#FF6C4D] hover:text-white transition-colors text-center"
                >
                  Contact Me
                </button>
              </div>
              <div className="flex flex-wrap gap-3 mt-8 justify-center md:justify-start">
                <a href="https://github.com/adam6272" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">GitHub</a>
                <span className="text-[#2A2E35]">|</span>
                <a href="https://www.linkedin.com/in/adam-lederer-124177145/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">LinkedIn</a>
                <span className="text-[#2A2E35]">|</span>
                <a href="mailto:adamlederer6272@gmail.com" className="text-gray-300 hover:text-white transition-colors">Email</a>
              </div>
            </div>
          </div>
          <div className="flex-1 flex justify-center items-center md:pr-20 w-full md:w-auto hidden md:flex" style={{ zIndex: 2, position: 'relative' }}>
            <div className={`diamond-container transition-opacity duration-1000 ${isImageVisible ? 'opacity-100' : 'opacity-0'}`}>
              <div className="diamond-wrapper">
                <img src={adam} alt="Adam Lederer" className="diamond-image" />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 mb-8 flex justify-center">
          <button type="button" aria-label="Scroll to next section" className="animate-bounce cursor-pointer" onClick={() => handleNavClick('about')}>
            <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8l8 8 8-8" />
            </svg>
          </button>
        </div>
      </header>

      <section className="bg-[#14161B] text-white p-4 md:p-8" id="about">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 inline-block border-b-2 border-[#FF6C4D] pb-2">
              &#47;&#42; About &#42;&#47;
            </h2>
            <p className="max-w-3xl mx-auto text-gray-300 leading-relaxed">
              I enjoy solving high-impact problems where product quality and security both matter. My background combines software engineering and cybersecurity competition experience, so I care deeply about building things that are both useful and resilient.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {quickStats.map((stat) => (
              <div key={stat.label} className="section-card text-center">
                <p className="text-2xl md:text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-gray-300 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="section-card">
            <h3 className="text-xl font-semibold text-white mb-4">Core Skills</h3>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <span key={skill} className="bg-[#17191E] text-sm text-gray-200 px-3 py-2 rounded-full border border-[#2A2E35]">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A1D24] text-white p-4 md:p-8" id="portfolio">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 inline-block border-b-2 border-[#FF6C4D] pb-2">
            &#47;&#42; Portfolio &#42;&#47;
          </h2>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#FF6C4D] text-white'
                  : 'bg-[#1F2329] text-gray-300 border border-[#2A2E35] hover:border-[#FF6C4D]'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedCategory('coding')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === 'coding'
                  ? 'bg-[#FF6C4D] text-white'
                  : 'bg-[#1F2329] text-gray-300 border border-[#2A2E35] hover:border-[#FF6C4D]'
              }`}
            >
              Coding
            </button>
            <button
              onClick={() => setSelectedCategory('cybersecurity')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === 'cybersecurity'
                  ? 'bg-[#FF6C4D] text-white'
                  : 'bg-[#1F2329] text-gray-300 border border-[#2A2E35] hover:border-[#FF6C4D]'
              }`}
            >
              Cyber Security
            </button>
          </div>
        </div>
        <div className="flex flex-wrap -mx-2 md:-mx-4">
          {filteredProjects.map((project, index) => (
            <ProjectCard key={project.title} project={project} index={index} />
          ))}
        </div>
      </section>

      <section className="bg-[#17191E] text-white p-4 md:p-8 flex justify-center items-center min-h-[45vh]" id="contact">
        <div className="text-center max-w-xl">
          <h2 className="text-3xl md:text-4xl font-bold text-white inline-block border-b-2 border-[#FF6C4D] pb-2">
            &#47;&#42; Contact &#42;&#47;
          </h2>
          <p className="mt-5 text-gray-300">
            Interested in collaborating, hiring, or discussing security-focused product work?
          </p>
          <a
            href="mailto:adamlederer6272@gmail.com"
            className="inline-block mt-6 bg-[#FF6C4D] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#FF8C6D] transition-colors"
          >
            adamlederer6272@gmail.com
          </a>
        </div>
      </section>

      <footer className="bg-[#121419] text-center text-gray-400 py-5 border-t border-[#1F2329]">
        <p>© {currentYear} Adam Lederer. Built with React.</p>
      </footer>
    </div>
  );
}

export default App;
